import {E, GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Column, Row, Select, Text} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {ES, Link, RunInAction, RunInAction_Set, TimeToString} from "web-vcore";
import {DreamScene, GetDreamAnchorEntity, GetDreamTitles, GetScenesForJournalEntryAndSegment} from "../../Server/DB/journalEntries/$journalEntry.js";
import {JournalEntry, JournalSegment} from "../../Server/DB/journalEntries/@JournalEntry.js";
import {store} from "../../Store/index.js";
import {JournalSort} from "../../Store/main/journal.js";
import {SpinnerForPercent} from "../../Utils/ReactComponents/SpinnerForPercent.js";
import {GetDreamsToShow_EntryList} from "./DreamList.js";
import {JournalSegmentUI} from "./JournalEntry/SegmentUI.js";
import {JournalDisplayModeSwitcher} from "./JournalUI.js";
import {GetMapObjectsPerDreamScene, ScenePreview} from "./SceneList/ScenePreview.js";
import {GetMapObjects} from "../../Server/DB/mapObjects.js";

export const GetScenesToShow_EntryList = (entries: JournalEntry[], sort: JournalSort, sort_desc: boolean, onlyLucids: boolean, onlyIntegratable: boolean, onlyNonEmpty: boolean)=>{
	const dreamsMatching = GetDreamsToShow_EntryList(entries, sort, sort_desc, onlyLucids, onlyIntegratable, onlyNonEmpty);
	return dreamsMatching.flatMap(dream=>{
		if (dream.segment.id == null) return []; // just ignore super old segments without ids
		const scenes = GetScenesForJournalEntryAndSegment(dream.entry, dream.segment);
		// if there are multiple scenes in a segment, order them as though the later-listed is the "newer" one (since that's how they were likely added/edited)
		// (this way, reversing the sort order will give the exact inverse ordering, which matches expectations better)
		return scenes[sort_desc ? "OrderByDescending" : "OrderBy"]((scene, index)=>index);
	});
};

export const SceneList = observer_mgl((props: {entries: JournalEntry[]})=>{
	const {entries} = props;
	const uiState1 = store.main.journal;
	const uiState2 = store.main.engine.journey;

	const scenesToShow = GetScenesToShow_EntryList(entries, uiState1.sortBy, uiState1.sort_desc, uiState1.sceneList_onlyLucids, uiState1.sceneList_onlyIntegratable, true);
	const mapObjectsPerDreamScene = GetMapObjectsPerDreamScene(store.main.maps.selectedMapID);
	const scenesToShow_mapped = scenesToShow.filter(a=>mapObjectsPerDreamScene.has(a.ID()));
	const selectedScene = uiState1.sceneList_selectedScene ? scenesToShow.find(a=>a.ID() == uiState1.sceneList_selectedScene) : null;

	return (
		<Column style={ES({height: "100%", flex: 30})}>
			<Row className="clickThrough" style={{flexWrap: "wrap", gap: 5, padding: 10, background: "rgba(0,0,0,.7)", borderRadius: "10px 10px 0 0"}}>
				<JournalDisplayModeSwitcher/>
				<CheckBox text="Only lucids" value={uiState1.sceneList_onlyLucids} onChange={val=>RunInAction_Set(()=>uiState1.sceneList_onlyLucids = val)}/>
				<CheckBox text="Only integratable" value={uiState1.sceneList_onlyIntegratable} onChange={val=>RunInAction_Set(()=>uiState1.sceneList_onlyIntegratable = val)}/>
				<Row>
					<Text>Sort:</Text>
					<Select ml={5} options={GetEntries(JournalSort, "ui")} value={uiState1.sortBy} onChange={val=>RunInAction_Set(()=>uiState1.sortBy = val)}/>
					<Button ml={5} mdIcon={uiState1.sort_desc ? "arrow-down" : "arrow-up"} onClick={()=>{
						RunInAction_Set(()=>uiState1.sort_desc = !uiState1.sort_desc);
					}}/>
				</Row>
				<CheckBox text="Side-panel" value={uiState1.sceneList_sidePanel_show} onChange={val=>RunInAction_Set(()=>uiState1.sceneList_sidePanel_show = val)}/>
				<Text ml={5}>Size:</Text>
				<SpinnerForPercent ml={5} value={uiState1.sceneList_sidePanel_size} onChange={val=>RunInAction_Set(()=>uiState1.sceneList_sidePanel_size = val)}/>
				{scenesToShow.length > 0 &&
				<Text ml={5}>Mapping progress: {scenesToShow_mapped.length}/{scenesToShow.length} ({(scenesToShow_mapped.length / scenesToShow.length).ToPercentStr(1)})</Text>}
			</Row>
			<Row style={{flex: 1, minHeight: 0}}>
				<ScrollView style={{flex: (1 - uiState1.sceneList_sidePanel_size).ToPercent()}} contentStyle={E(
					{
						borderRadius: "0 0 10px 10px",
						//backgroundColor: "rgba(30,30,30,.7)",
						backgroundColor: "rgba(0,0,0,1)",
						display: "flex", flexWrap: "wrap", gap: "0 5px",
					},
					uiState1.textPreviewMultiline && {minWidth: null},
				)} content_onClick={e=>{
					if (e.target != e.currentTarget) return;
					RunInAction("SceneList.background.onClick", ()=>store.main.journal.sceneList_selectedScene = null);
				}}>
					{scenesToShow.length == 0 && <div style={{textAlign: "center", fontSize: 18}}>No matching scenes.</div>}
					{scenesToShow.map((scene, index)=>{
						return <ScenePreview key={scene.ID()} scene={scene}/>;
					})}
				</ScrollView>
				{uiState1.sceneList_sidePanel_show &&
				<ScrollView style={{flex: uiState1.sceneList_sidePanel_size.ToPercent()}}>
					{selectedScene != null && <>
						<JournalSegmentUI
							key={selectedScene.segment.id ?? selectedScene.journalEntry.id} // ensure fresh comp per dream (prevent failed-text-load/text-overwrite)
							entry={selectedScene.journalEntry} segment={selectedScene.segment} segments={selectedScene.journalEntry.segments}
							showActionsRow={false} showStartTime={true}/>
						<Link actionFunc={s=>{
							s.main.journal.selectedJournalEntry = selectedScene.journalEntry.id;
						}}>
							<Button text="Show full journal-entry"/>
						</Link>
					</>}
				</ScrollView>}
			</Row>
		</Column>
	);
});